import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Sparkles, Loader2, TrendingUp, CheckCircle2, Trash2, RefreshCw,
  Calculator, ListPlus, Database, LineChart, ArrowUpRight, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const CONF = {
  high: { label: 'High', cls: 'text-emerald-500 border-emerald-500/40 bg-emerald-500/10' },
  medium: { label: 'Medium', cls: 'text-amber-500 border-amber-500/40 bg-amber-500/10' },
  low: { label: 'Low', cls: 'text-muted-foreground border-border bg-muted/40' },
};

export default function LearnedRatesPage() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [promotingAll, setPromotingAll] = useState(false);
  const [targetMargin, setTargetMargin] = useState(20);
  const [savingMargin, setSavingMargin] = useState(false);

  // estimate dialog
  const [estOpen, setEstOpen] = useState(false);
  const [scope, setScope] = useState('');
  const [total, setTotal] = useState('');
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // trend dialog
  const [trendOpen, setTrendOpen] = useState(false);
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const openTrend = async (rate) => {
    setTrendOpen(true);
    setTrend(null);
    setTrendLoading(true);
    try {
      const res = await api.get(`/learned-rates/${rate.id}/trend`);
      setTrend(res.data);
    } catch (e) {
      toast.error('Failed to load price trend');
      setTrendOpen(false);
    } finally {
      setTrendLoading(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get('/learned-rates');
      setRates(res.data || []);
      try {
        const m = await api.get('/settings/margin');
        setTargetMargin(m.data?.target_margin_pct ?? 20);
      } catch (_) { /* optional */ }
    } catch (e) {
      toast.error('Failed to load learned rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const promoteAll = async () => {
    setPromotingAll(true);
    try {
      const res = await api.post('/learned-rates/promote-all');
      toast.success(res.data.promoted > 0
        ? `Added ${res.data.promoted} high-confidence rate(s) to your pricing schedule`
        : 'No new high-confidence rates to add');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote rates');
    } finally {
      setPromotingAll(false);
    }
  };

  const saveMargin = async (val) => {
    setSavingMargin(true);
    try {
      const res = await api.put('/settings/margin', { target_margin_pct: Number(val) });
      setTargetMargin(res.data.target_margin_pct);
      toast.success(`Target margin set to ${res.data.target_margin_pct}%`);
    } catch (e) {
      toast.error('Failed to save target margin');
    } finally {
      setSavingMargin(false);
    }
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await api.post('/learned-rates/backfill');
      toast.success(`Learned ${res.data.line_items_learned} line items from ${res.data.candidates_scanned} POs`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const promote = async (rate) => {
    setBusyId(rate.id);
    try {
      await api.post(`/learned-rates/${rate.id}/promote`);
      toast.success(`"${rate.description}" added to pricing schedule`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote');
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (rate) => {
    setBusyId(rate.id);
    try {
      await api.delete(`/learned-rates/${rate.id}`);
      setRates((prev) => prev.filter((r) => r.id !== rate.id));
      toast.success('Dismissed');
    } catch (e) {
      toast.error('Failed to dismiss');
    } finally {
      setBusyId(null);
    }
  };

  const runEstimate = async () => {
    if (!scope.trim()) { toast.error('Describe the scope of works first'); return; }
    setEstimating(true);
    setEstimate(null);
    try {
      const res = await api.post('/learned-rates/estimate', {
        description: scope,
        total_amount: total ? parseFloat(total) : null,
      });
      setEstimate(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Estimate failed');
    } finally {
      setEstimating(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="learned-rates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            Schedule of Rates
            <span className="text-xs font-semibold px-2 py-1 rounded-full brand-gradient text-white flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI
            </span>
          </h1>
          <p className="text-muted-foreground mt-1">Rates the assistant learned from your priced POs — review and add the good ones to your pricing schedule</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={backfill} disabled={backfilling} data-testid="backfill-btn">
            {backfilling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
            Learn from past POs
          </Button>
          <Button variant="outline" onClick={promoteAll} disabled={promotingAll} data-testid="promote-all-btn">
            {promotingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Add all high-confidence
          </Button>
          <Button className="brand-gradient text-white" onClick={() => { setEstOpen(true); setEstimate(null); }} data-testid="open-estimate-btn">
            <Calculator className="h-4 w-4 mr-2" /> Estimate a lump sum
          </Button>
        </div>
      </div>

      {/* Target margin control — powers the quote-builder margin guardrail */}
      <Card className="surface">
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Target margin</span>
          <span className="text-xs text-muted-foreground">Warn me in the quote builder if a line's margin drops below</span>
          <div className="flex items-center gap-2">
            <Input
              type="number" min="0" max="95"
              defaultValue={targetMargin}
              key={targetMargin}
              onBlur={(e) => { const v = Number(e.target.value); if (v !== targetMargin) saveMargin(v); }}
              className="w-20 bg-input border-border h-8 text-sm"
              data-testid="target-margin-input"
            />
            <span className="text-sm font-semibold">%</span>
            {savingMargin && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : rates.length === 0 ? (
        <Card className="surface">
          <CardContent className="p-10 text-center">
            <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-semibold">No learned rates yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              As you import POs that list prices per line item, the assistant learns what each item is worth.
              Already imported some? Hit <span className="text-foreground font-medium">"Learn from past POs"</span> to backfill.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="surface">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="learned-rates-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="px-4 py-3 font-medium">Line item</th>
                    <th className="px-4 py-3 font-medium text-right">Avg price</th>
                    <th className="px-4 py-3 font-medium text-right">Last price</th>
                    <th className="px-4 py-3 font-medium text-right">Margin</th>
                    <th className="px-4 py-3 font-medium text-center">Seen</th>
                    <th className="px-4 py-3 font-medium text-center">Confidence</th>
                    <th className="px-4 py-3 font-medium">Last seen</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`rate-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium flex items-center gap-2">
                          {r.description}
                          {r.promoted && (
                            <Badge variant="outline" className="text-emerald-500 border-emerald-500/40 text-[10px]">In pricing</Badge>
                          )}
                        </div>
                        {r.unit && <div className="text-xs text-muted-foreground">per {r.unit}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{money(r.avg_price)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{money(r.last_price)}</td>
                      <td className="px-4 py-3 text-right" data-testid={`rate-margin-${r.id}`}>
                        {r.margin_pct != null ? (
                          <span className="inline-flex flex-col items-end">
                            <span className={`text-sm font-semibold ${r.margin_pct >= 25 ? 'text-emerald-500' : r.margin_pct >= 10 ? 'text-amber-500' : 'text-red-500'}`}>
                              {r.margin_pct}%
                            </span>
                            <span className="text-[10px] text-muted-foreground">cost {money(r.cost)}</span>
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground italic">set cost</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="secondary" data-testid={`rate-seen-${r.id}`}>{r.times_seen}×</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${CONF[r.confidence || 'low'].cls}`}
                          title={r.spread_pct ? `Prices vary ${r.spread_pct}% ($${r.price_min}–$${r.price_max})` : 'Single data point'}
                          data-testid={`rate-confidence-${r.id}`}
                        >
                          <ShieldCheck className="h-3 w-3" /> {CONF[r.confidence || 'low'].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {r.last_seen_at ? format(parseISO(r.last_seen_at), 'd MMM yyyy') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openTrend(r)}
                            title="Price trend by supplier"
                            data-testid={`trend-btn-${r.id}`}
                          >
                            <LineChart className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            disabled={busyId === r.id || r.promoted}
                            onClick={() => promote(r)}
                            data-testid={`promote-btn-${r.id}`}
                          >
                            {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListPlus className="h-3.5 w-3.5 mr-1" />}
                            {r.promoted ? 'Added' : 'Add to pricing'}
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            disabled={busyId === r.id}
                            onClick={() => dismiss(r)}
                            data-testid={`dismiss-btn-${r.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lump-sum estimate dialog */}
      <Dialog open={estOpen} onOpenChange={setEstOpen}>
        <DialogContent className="max-w-2xl" data-testid="estimate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-primary" /> Estimate a lump-sum PO
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Some POs arrive as a single lump sum with no per-item pricing. Paste the scope and the assistant
              will break it down and price each part from your learned rates. This is a non-binding estimate.
            </p>
            <div>
              <Label htmlFor="scope">Scope of works</Label>
              <Textarea
                id="scope" rows={5} value={scope} onChange={(e) => setScope(e.target.value)}
                placeholder="e.g. Make safe to bathroom: remove damaged vanity, isolate power, install temporary GPO, seal leak, dispose of waste…"
                className="mt-1 bg-input border-border" data-testid="estimate-scope"
              />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="total">PO total (optional)</Label>
                <Input
                  id="total" type="number" value={total} onChange={(e) => setTotal(e.target.value)}
                  placeholder="Scale the estimate to fit this amount"
                  className="mt-1 bg-input border-border" data-testid="estimate-total"
                />
              </div>
              <Button className="brand-gradient text-white" onClick={runEstimate} disabled={estimating} data-testid="run-estimate-btn">
                {estimating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Estimate
              </Button>
            </div>

            {estimate && (
              <div className="rounded-lg border border-border overflow-hidden" data-testid="estimate-result">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground bg-muted/40">
                      <th className="px-3 py-2 font-medium">Item</th>
                      <th className="px-3 py-2 font-medium text-center">Qty</th>
                      <th className="px-3 py-2 font-medium text-right">Unit</th>
                      <th className="px-3 py-2 font-medium text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimate.items.map((it, i) => (
                      <tr key={i} className="border-t border-border/50">
                        <td className="px-3 py-2">
                          {it.description}
                          {it.source === 'unknown' && <Badge variant="outline" className="ml-2 text-[10px] text-amber-500 border-amber-500/40">no rate yet</Badge>}
                        </td>
                        <td className="px-3 py-2 text-center">{it.quantity}</td>
                        <td className="px-3 py-2 text-right">{money(it.estimated_unit_price)}</td>
                        <td className="px-3 py-2 text-right font-medium">{money(it.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border bg-muted/30">
                      <td className="px-3 py-2 font-semibold" colSpan={3}>Estimated total</td>
                      <td className="px-3 py-2 text-right font-bold text-primary" data-testid="estimate-total-value">{money(estimate.estimated_total)}</td>
                    </tr>
                  </tfoot>
                </table>
                <p className="text-xs text-muted-foreground px-3 py-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {estimate.note} {estimate.matched_count} of {estimate.items.length} items matched a learned rate.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier price-trend dialog */}
      <Dialog open={trendOpen} onOpenChange={setTrendOpen}>
        <DialogContent className="max-w-2xl" data-testid="trend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LineChart className="h-5 w-5 text-primary" /> Price trend by supplier
            </DialogTitle>
          </DialogHeader>
          {trendLoading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : trend ? (
            <div className="space-y-4">
              <p className="text-sm"><span className="font-semibold">{trend.description}</span>
                <span className="text-muted-foreground"> · {trend.series.length} price points</span></p>

              <div className="space-y-2">
                {trend.suppliers.map((s, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`trend-supplier-${i}`}>
                    <div>
                      <div className="font-medium text-sm">{s.supplier}</div>
                      <div className="text-xs text-muted-foreground">{s.count} PO{s.count > 1 ? 's' : ''} · {money(s.first_price)} → {money(s.last_price)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-bold flex items-center gap-1 justify-end ${s.change_pct > 0 ? 'text-amber-500' : s.change_pct < 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                        {s.change_pct > 0 && <ArrowUpRight className="h-3.5 w-3.5" />}
                        {s.change_pct > 0 ? '+' : ''}{s.change_pct}%
                      </div>
                      {s.creeping && (
                        <span className="text-[10px] font-semibold text-amber-500 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 rounded-full" data-testid={`trend-creeping-${i}`}>creeping up</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-border overflow-hidden">
                <div className="text-xs text-muted-foreground px-3 py-1.5 bg-muted/40 font-medium">Full history</div>
                <div className="max-h-48 overflow-y-auto">
                  {[...trend.series].reverse().map((pt, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs border-t border-border/50">
                      <span className="text-muted-foreground">{format(parseISO(pt.at), 'd MMM yyyy')} · {pt.supplier}</span>
                      <span className="font-mono font-medium">{money(pt.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
